import { useEffect, useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { Category, Item, ItemInput, Supplier } from '@iobiz/shared'
import {
  UNITS,
  UNIT_LABELS,
  format,
  itemInputSchema,
  PRIX_ACHAT_OBLIGATOIRE,
  prixAchatManquant,
  marginRates,
  money,
  prixTTC,
  uuidv7,
  type CurrencyCode,
} from '@iobiz/shared'
import { ApiError } from '../../api/client.js'
import { PhotoGallery } from '../../ui/PhotoGallery.js'
import { useTaxes } from '../../data/hooks.js'
import { genererCodeBarresEan13 } from '@iobiz/shared'
import { flattenCategories } from './api.js'

/**
 * Référence d'article, engendrée par le système.
 *
 * ⚠️ Côté CLIENT, et non serveur : un article doit pouvoir être créé hors
 * ligne, donc porter sa référence avant tout échange. Un compteur séquentiel
 * exigerait une coordination impossible sans réseau ; on dérive donc le
 * suffixe d'un UUIDv7, dont la partie haute est temporelle : les références
 * restent lisibles et croissent dans l'ordre de création.
 */
/**
 * Un groupe de champs sous un intertitre — même composant que la modale
 * client, pour que les deux formulaires se parcourent de la même façon.
 */
function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Box component="section" sx={{ mt: 5, '&:first-of-type': { mt: 0 } }}>
      <Typography
        component="h3"
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', letterSpacing: '.08em', mb: 2 }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {children}
      </Box>
    </Box>
  )
}

/** Largeur commune au sélecteur de type et à la référence, qui se superposent. */
const CHAMP_ENTETE = 220

function genererReference(kind: string): string {
  const prefixe = kind === 'service' ? 'SRV' : 'ART'
  const suffixe = uuidv7().replace(/-/g, '').slice(0, 10)
  return `${prefixe}-${parseInt(suffixe, 16).toString(36).toUpperCase().slice(-6)}`
}
import { saveItem } from '../../data/repositories/catalog.write.js'

/**
 * Création et édition d'un article.
 *
 * Produit et service partagent ce formulaire : basculer le type masque le
 * suivi de stock plutôt que d'ouvrir un autre écran (ADR-06). L'utilisateur
 * voit deux entrées de menu, mais une seule façon de saisir.
 */

interface Props {
  open: boolean
  storeId: string
  item?: Item | null
  categories: Category[]
  suppliers: Supplier[]
  defaultKind?: 'product' | 'service'
  onClose: () => void
  onSaved: (item: Item) => void
}

type FormState = Record<string, string>

const EMPTY: FormState = {
  kind: 'product',
  name: '',
  sku: '',
  barcode: '',
  description: '',
  categoryId: '',
  taxId: '',
  supplierId: '',
  unit: 'piece',
  // ⚠️ VIDE et non « 0 » : un zéro pré-rempli passe pour une valeur saisie, et
  // c'est ainsi que des produits sans prix d'achat se sont enregistrés.
  purchasePrice: '',
  salePrice: '0',
  currencyCode: 'XAF',
  minStockLevel: '0',
  durationMinutes: '',
  status: 'active',
}

export function ItemFormDialog({
  open,
  storeId,
  item,
  categories,
  suppliers,
  defaultKind = 'product',
  onClose,
  onSaved,
}: Props): JSX.Element {
  const editing = Boolean(item)

  const [form, setForm] = useState<FormState>(EMPTY)

  // ⚠️ APRÈS la déclaration de `form` : placé avant, ce calcul le lisait
  // dans sa zone morte temporelle et faisait planter toute la boîte.
  const taxes = useTaxes(storeId)
  /**
   * Taux du choix courant.
   *
   * ⚠️ Résolu depuis la liste RÉPLIQUÉE, pas depuis le serveur : le formulaire
   * doit rester utilisable hors ligne, et un taux introuvable calculerait un
   * prix TTC égal au HT sans que rien ne le signale.
   */
  const tauxChoisi = taxes.find((t) => t.id === form['taxId'])?.rate ?? 0
  const formatMonnaie = (v: number): string =>
    format(money(v, (form['currencyCode'] || 'XAF') as CurrencyCode), 'fr-FR')
  const [images, setImages] = useState<string[]>([])
  const [trackStock, setTrackStock] = useState(true)
  const [stockInitial, setStockInitial] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setErrors({})
    setBanner(null)
    if (item) {
      setForm({
        kind: item.kind,
        name: item.name,
        sku: item.sku ?? '',
        barcode: item.barcode ?? '',
        description: item.description ?? '',
        categoryId: item.categoryId ?? '',
        taxId: item.taxId ?? '',
        supplierId: item.supplierId ?? '',
        unit: item.unit,
        purchasePrice: String(item.purchasePrice),
        salePrice: String(item.salePrice),
        currencyCode: item.currencyCode ?? 'XAF',
        minStockLevel: String(item.minStockLevel),
        durationMinutes: item.durationMinutes ? String(item.durationMinutes) : '',
        visibleSurSite: item.visibleSurSite ? 'oui' : '',
        status: item.status,
      })
      // Reprise de l'existant : un article n'ayant qu'un visuel donne un
      // tableau d'un élément.
      setImages(item.images?.length ? item.images : item.imageUrl ? [item.imageUrl] : [])
      setTrackStock(item.trackStock)
      setStockInitial('')
    } else {
      setForm({
        ...EMPTY,
        kind: defaultKind,
        sku: genererReference(defaultKind),
        // Un service ne se scanne pas : pas de code-barres.
        barcode: defaultKind === 'service' ? '' : genererCodeBarresEan13(),
      })
      setImages([])
      setTrackStock(defaultKind === 'product')
      setStockInitial('')
    }
  }, [open, item, defaultKind])

  function set(field: string, value: string): void {
    setForm((f) => ({ ...f, [field]: value }))
    if (errors[field]) setErrors(({ [field]: _r, ...rest }) => rest)
  }

  function setKind(kind: string): void {
    set('kind', kind)
    // Le préfixe suit le type ; le suffixe, lui, ne bouge pas — sans quoi la
    // référence affichée changerait sous les yeux de l'utilisateur à chaque
    // hésitation entre produit et service.
    setForm((f) => {
      const suffixe = (f['sku'] ?? '').split('-')[1]
      const barcode = kind === 'service' ? '' : f['barcode'] || genererCodeBarresEan13()
      if (!suffixe) return { ...f, barcode }
      return { ...f, sku: `${kind === 'service' ? 'SRV' : 'ART'}-${suffixe}`, barcode }
    })
    // ⚠️ La catégorie choisie appartenait à l'AUTRE famille : elle vient de
    // disparaître de la liste. La garder laisserait le champ afficher un vide
    // trompeur et enregistrerait un classement que l'écran ne montre plus.
    setForm((f) => {
      const choisie = flattenCategories(categories).find((o) => o.id === f['categoryId'])
      const famille = kind === 'service' ? 'service' : 'product'
      return choisie && choisie.kind !== famille ? { ...f, categoryId: '' } : f
    })

    // Un service n'a pas de stock : le basculement le reflète immédiatement,
    // sans attendre le refus du serveur.
    if (kind === 'service') setTrackStock(false)
    else if (!editing) setTrackStock(true)

    // ⚠️ L'obligation du prix d'achat ne vaut que pour un PRODUIT : passé en
    // service, l'erreur resterait affichée sous un champ devenu « Coût de
    // revient », pour une règle qui ne s'applique plus.
    if (kind === 'service') setErrors(({ purchasePrice: _p, ...rest }) => rest)
  }

  async function submit(): Promise<void> {
    const parsed = itemInputSchema.safeParse({
      ...form,
      purchasePrice: Number(form['purchasePrice'] || 0),
      salePrice: Number(form['salePrice'] || 0),
      minStockLevel: Number(form['minStockLevel'] || 0),
      /**
       * ⚠️ La durée ne part QUE pour un service, et seulement si elle est
       * saisie. Un produit n'a pas de durée ; en laisser traîner une, invisible
       * dans le formulaire, la ferait apparaître à l'agenda le jour où
       * l'article bascule en service — avec une valeur que personne n'a
       * choisie.
       */
      durationMinutes:
        form['kind'] === 'service' && form['durationMinutes'] ? form['durationMinutes'] : undefined,
      trackStock,
      visibleSurSite: form['visibleSurSite'] === 'oui',
      // Create-only : le serveur en fera un mouvement d'entrée.
      initialStock: item ? undefined : Number(stockInitial || 0),
      images,
      imageUrl: images[0] ?? undefined,
    })

    // ⭐ Vérifié en même temps que le reste, pour que toutes les erreurs
    // s'affichent d'un coup plutôt qu'une par tentative.
    const sansPrix = prixAchatManquant({
      kind: form['kind'] === 'service' ? 'service' : 'product',
      purchasePrice: Number(form['purchasePrice'] || 0),
    })
    if (!parsed.success || sansPrix) {
      const fieldErrors: Record<string, string> = {}
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          fieldErrors[issue.path.join('.') || 'form'] ??= issue.message
        }
      }
      if (sansPrix) fieldErrors['purchasePrice'] ??= PRIX_ACHAT_OBLIGATOIRE
      setErrors(fieldErrors)
      return
    }

    setSaving(true)
    setBanner(null)
    try {
      const input = parsed.data as ItemInput
      const saved = item ? await saveItem(storeId, input, item) : await saveItem(storeId, input)

      onSaved(saved)
      onClose()
    } catch (err) {
      if (err instanceof ApiError) {
        setBanner(err.message)
        setErrors(err.fieldErrors())
      } else {
        setBanner('Enregistrement impossible. Vérifiez votre connexion.')
      }
    } finally {
      setSaving(false)
    }
  }

  const isService = form['kind'] === 'service'
  /**
   * ⭐ Seules les catégories de la FAMILLE de l'article. Un service rangé dans
   * « Encens » ne serait jamais une colonne de l'agenda, et un produit rangé
   * dans « Coiffure » y ferait apparaître une place à réserver.
   */
  const famille = isService ? 'service' : 'product'
  const toutes = flattenCategories(categories)
  const options = toutes.filter((o) => o.kind === famille)
  /**
   * ⚠️ La catégorie ACTUELLE, quand elle appartient à l'autre famille.
   *
   * Sans elle, le champ s'affichait vide sur un article déjà classé — vide
   * trompeur : l'article a bien une catégorie, c'est elle qui n'est plus de sa
   * famille. On la montre, signalée, pour que le reclassement soit un choix et
   * non une découverte.
   */
  const horsFamille = toutes.find((o) => o.id === form['categoryId'] && o.kind !== famille)

  // Marge indicative, calculée à la saisie : le gérant voit tout de suite si
  // son prix de vente tient. Les DEUX taux sont nommés — les confondre fausse
  // toutes les décisions de prix (voir marginRates).
  const purchase = Number(form['purchasePrice'] || 0)
  const sale = Number(form['salePrice'] || 0)
  const taux = marginRates(purchase, sale)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {editing ? 'Modifier l’article' : isService ? 'Nouveau service' : 'Nouveau produit'}
      </DialogTitle>

      <DialogContent dividers>
        {banner && (
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {banner}
          </Alert>
        )}

        {/* Visuels à gauche, décisions d'entête à leur droite. `flexWrap`
            plutôt qu'une largeur figée : à cinq vignettes sur un écran étroit,
            le bloc de droite passe dessous au lieu d'être écrasé. */}
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap', mb: 5 }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Visuels
            </Typography>
            <PhotoGallery values={images} onChange={setImages} />
          </Box>

          <Box sx={{ flexShrink: 0, width: CHAMP_ENTETE }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Type d’article
            </Typography>
            {/* Le groupe de boutons et la référence font la MÊME largeur :
                deux contrôles superposés de largeurs différentes donnent une
                colonne bancale, alors qu'ils forment un même bloc de décision. */}
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={form['kind']}
              onChange={(_, v: string | null) => v && setKind(v)}
              sx={{ width: '100%' }}
            >
              <ToggleButton value="product">Produit</ToggleButton>
              <ToggleButton value="service">Service</ToggleButton>
            </ToggleButtonGroup>

            {/* ⚠️ `fullWidth` et NON `display: block` : forcer le bloc sur le
                FormControl empêche l'InputBase, en `inline-flex`, de s'étirer —
                il se dimensionne alors à son contenu et le champ paraît plus
                étroit que le sélecteur juste au-dessus. */}
            <TextField
              label="Référence"
              fullWidth
              value={form['sku']}
              InputProps={{ readOnly: true }}
              size="small"
              sx={{ mt: 3 }}
              error={Boolean(errors['sku'])}
              helperText={errors['sku'] ?? 'Engendrée automatiquement'}
            />
          </Box>
        </Box>

        <Section title="Identification">
          <TextField
            label="Nom"
            required
            value={form['name']}
            onChange={(e) => set('name', e.target.value)}
            error={Boolean(errors['name'])}
            helperText={errors['name']}
            sx={{ gridColumn: '1 / -1' }}
          />

          <TextField
            label="Code-barres"
            value={form['barcode']}
            onChange={(e) => set('barcode', e.target.value)}
            error={Boolean(errors['barcode'])}
            helperText={
              errors['barcode'] ??
              (isService
                ? 'Sans objet pour un service'
                : 'EAN-13 interne engendré — modifiable si l’article en porte déjà un')
            }
            disabled={isService}
            InputProps={{
              /**
               * ⭐ Le bouton n'apparaît QUE si le champ est vide. Un article créé
               * avant la génération automatique, ou importé sans code, se répare
               * ainsi sans quitter la fiche — c'est ici qu'on s'en aperçoit.
               *
               * ⚠️ Il ne REMPLACE jamais un code existant : écraser celui du
               * fabricant d'une pression donnerait deux identités au même
               * article, et la douchette désignerait un produit inconnu.
               */
              ...(!isService && !form['barcode']
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => set('barcode', genererCodeBarresEan13())}
                        >
                          Engendrer
                        </Button>
                      </InputAdornment>
                    ),
                  }
                : {}),
            }}
          />
        </Section>

        <Section title="Site vitrine">
          {/* ⭐ Le catalogue de gestion et la vitrine ne montrent pas la même
              chose : on vend au comptoir des articles qu'on n'expose pas en
              ligne. La case le dit article par article.
              ⚠️ Décochée par défaut — publier un site ne doit pas déverser tout
              le catalogue sur la place publique à l'insu du commerçant. */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form['visibleSurSite'] === 'oui'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  set('visibleSurSite', e.target.checked ? 'oui' : '')
                }
              />
            }
            label="Visible sur le site vitrine"
          />
        </Section>

        <Section title="Classement">
          <TextField
            select
            label="Catégorie"
            value={form['categoryId']}
            onChange={(e) => set('categoryId', e.target.value)}
          >
            <MenuItem value="">Aucune</MenuItem>
            {options.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ pl: 2 + o.depth * 3 }}>
                {o.label.trim()}
              </MenuItem>
            ))}
            {horsFamille && (
              <MenuItem value={horsFamille.id}>{horsFamille.label.trim()} — à reclasser</MenuItem>
            )}
            {options.length === 0 && !horsFamille && (
              <MenuItem value="" disabled>
                Aucune catégorie de {isService ? 'services' : 'produits'} — créez-en une dans
                Catalogue › Catégories
              </MenuItem>
            )}
          </TextField>

          {/* ⭐ La TVA se choisit ICI, à côté de la catégorie : c'est au moment
              de saisir un article qu'on sait à quel taux il est soumis. */}
          <TextField
            select
            label="TVA"
            value={form['taxId']}
            onChange={(e) => set('taxId', e.target.value)}
            helperText={
              tauxChoisi
                ? `Prix TTC : ${formatMonnaie(prixTTC(Number(form['salePrice']) || 0, tauxChoisi))}`
                : 'Aucune taxe appliquée'
            }
          >
            <MenuItem value="">Aucune</MenuItem>
            {taxes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name} — {t.rate} %
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Fournisseur"
            value={form['supplierId']}
            onChange={(e) => set('supplierId', e.target.value)}
            disabled={isService}
          >
            <MenuItem value="">Aucun</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.companyName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Unité"
            value={form['unit']}
            onChange={(e) => set('unit', e.target.value)}
          >
            {UNITS.map((u) => (
              <MenuItem key={u} value={u}>
                {UNIT_LABELS[u]}
              </MenuItem>
            ))}
          </TextField>

          {/* ⭐ La durée n'a de sens que pour une PRESTATION : c'est elle qui
              découpe l'agenda en créneaux. Facultative, car tout service ne se
              prend pas sur rendez-vous — une retouche au comptoir se facture
              sans occuper d'horaire. */}
          {isService && (
            <TextField
              label="Durée de la prestation"
              type="number"
              value={form['durationMinutes']}
              onChange={(e) => set('durationMinutes', e.target.value)}
              error={Boolean(errors['durationMinutes'])}
              helperText={
                errors['durationMinutes'] ??
                'En minutes. Sans durée, la prestation ne sera pas proposée à l’agenda.'
              }
              slotProps={{ htmlInput: { min: 5, max: 600, step: 5 } }}
            />
          )}

          <TextField
            select
            label="Statut"
            value={form['status']}
            onChange={(e) => set('status', e.target.value)}
          >
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
            <MenuItem value="discontinued">Arrêté</MenuItem>
          </TextField>

          <TextField
            label={isService ? 'Coût de revient HT' : "Prix d'achat HT"}
            required={!isService}
            type="number"
            value={form['purchasePrice']}
            onChange={(e) => set('purchasePrice', e.target.value)}
            error={Boolean(errors['purchasePrice'])}
            helperText={errors['purchasePrice']}
            InputProps={{
              endAdornment: <InputAdornment position="end">{form['currencyCode']}</InputAdornment>,
            }}
          />
          <TextField
            label="Prix de vente HT"
            type="number"
            value={form['salePrice']}
            onChange={(e) => set('salePrice', e.target.value)}
            error={Boolean(errors['salePrice'])}
            helperText={
              errors['salePrice'] ??
              (taux.markup !== null
                ? `Marge : ${taux.markup} %  ·  marque : ${taux.margin} %`
                : undefined)
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">{form['currencyCode']}</InputAdornment>,
            }}
          />
        </Section>

        <Section title="Description">
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={form['description']}
            onChange={(e) => set('description', e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Section>

        {!isService && (
          <Box component="section" sx={{ mt: 5 }}>
            <Typography
              component="h3"
              variant="overline"
              sx={{ display: 'block', color: 'text.secondary', letterSpacing: '.08em', mb: 2 }}
            >
              Stock
            </Typography>
            <Box sx={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch checked={trackStock} onChange={(e) => setTrackStock(e.target.checked)} />
                }
                label="Suivre le stock"
              />
              {trackStock && !item && (
                <TextField
                  label="Stock initial"
                  type="number"
                  size="small"
                  value={stockInitial}
                  onChange={(e) => setStockInitial(e.target.value)}
                  helperText="Enregistré comme une entrée"
                  sx={{ maxWidth: 200 }}
                />
              )}
              {trackStock && (
                <TextField
                  label="Seuil d’alerte"
                  type="number"
                  size="small"
                  value={form['minStockLevel']}
                  onChange={(e) => set('minStockLevel', e.target.value)}
                  helperText="Alerte quand le stock passe sous ce seuil"
                  sx={{ maxWidth: 220 }}
                />
              )}
            </Box>
          </Box>
        )}

        {isService && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 4, display: 'block' }}>
            Un service ne fait pas l’objet d’un suivi de stock.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
